import classNames from "classnames";
import Tooltip from "rc-tooltip";
import React, {
  Fragment,
  ReactElement,
  ReactNode,
  useEffect,
  useState,
} from "react";
import { FormattedMessage, defineMessages, useIntl } from "react-intl";
import { useSelector } from "react-redux";

import { getPolicyResultGOL } from "shared/cbt/api/policies";
import { TIME_PARAM_NAMES } from "shared/constants";
import { getCbtToken } from "shared/data/customerTokens";
import {
  isAcceptableFare,
  isLowestFare,
  policiesPassed,
} from "shared/hooks/travelPolicies";
import { numberWithSpaces } from "shared/lib/numberFormat";
import { formattedMessageParams } from "shared/messages";

import Img from "@components/FileServer/Img";
import { LowCostIndicator } from "@components/Flights/Indicators/LowCostIndicator";
import { OneWayIndicator } from "@components/Flights/Indicators/OneWayIndicator";
import { PrivateFareIndicator } from "@components/Flights/Indicators/PrivateFareIndicator";
import Modal from "@components/UI/Modal";

import { AppState } from "@shared/data/reducers";

const {
  policiesPassedTitle,
  policyDenied,
  lowestFareTitle,
  acceptableFare,
  corporateFare,
  preferredAirline,
  governmentPassenger,
  seamanFare,
  privateFareMsg,
} = defineMessages({
  policiesPassedTitle: formattedMessageParams(
    "TravelPolicy.policiesPassedTitle"
  ),
  policyDenied: formattedMessageParams("TravelPolicy.policyDenied"),
  lowestFareTitle: formattedMessageParams("TravelPolicy.lowestFare"),
  acceptableFare: formattedMessageParams("TravelPolicy.acceptableFare"),
  corporateFare: formattedMessageParams("TravelPolicy.corporateFare"),
  preferredAirline: formattedMessageParams("TravelPolicy.preferredAirline"),
  governmentPassenger: formattedMessageParams(
    "TravelPolicy.governmentPassenger"
  ),
  seamanFare: formattedMessageParams("TravelPolicy.seamanFare"),
  privateFareMsg: formattedMessageParams("TravelPolicy.privateFare"),
});

function PassIcon({ className }) {
  return (
    <div className="travel-policies-detail-popup__state-icon success">
      <Img
        src="/static/images/checkmark-white.svg"
        className={classNames(
          "travel-policies-detail-popup__success-state-img",
          className
        )}
      />
    </div>
  );
}

function FailedIcon({ className }) {
  return (
    <div className="travel-policies-detail-popup__state-icon fail">
      <Img
        src="/static/images/cross-white.svg"
        className={classNames(
          "travel-policies-detail-popup__failed-state-img",
          className
        )}
      />
    </div>
  );
}

export { policiesPassed };

function Yes() {
  return <FormattedMessage id="General.yes" />;
}

function No() {
  return <FormattedMessage id="General.no" />;
}

interface RuleParameter {
  OriginalValue: any;
  RequestValue: any;
  Result: boolean;
}

interface Rule {
  Id: string | number;
  Parameters: Record<string, RuleParameter>;
  Result: boolean;
}

interface Traveller {
  Email: string;
  Result: boolean;
  TravellerId: string | number;
  Type: string;
  GradeId: string | number;
  RulesResults: Rule[];
  [key: string]: any;
}

interface TravelPoliciesResultValue {
  Id?: string;
  Value?: Traveller[];
  Result?: boolean;
  Badges?: { LowestFare?: boolean; AcceptableFare?: boolean };
  [key: string]: any;
}

interface HumanValueProps {
  val: any;
  reservationVal?: any;
  name: string;
}

function HumanValue({ val, reservationVal, name }: HumanValueProps): ReactNode {
  if (val === true) return <Yes />;
  if (val === false) return <No />;

  if (TIME_PARAM_NAMES.includes(name)) {
    return (
      <span>
        {Math.floor(val / 60)} <FormattedMessage id="Units.hours" />{" "}
        {Math.round(val % 60)} <FormattedMessage id="Units.minutes" />
      </span>
    );
  }

  if (Array.isArray(val)) {
    if (val.length === 0) return "-";
    const maxCount = 5;
    const short = val.sort().slice(0, maxCount);

    return val.length > maxCount ? (
      <div className="flex items-center content-center">
        <span>
          {val.includes(reservationVal) ? reservationVal : val[0]} +
          {val.length - 1} <FormattedMessage id="General.more" />
        </span>
        <Tooltip overlay={val.join(", ")} trigger={["click"]}>
          <div className="travel-policies-detail-popup__info-icon">i</div>
        </Tooltip>
      </div>
    ) : (
      short.join(", ")
    );
  }

  if (!val) {
    return "-";
  }

  if (typeof val === "number") {
    return numberWithSpaces(Math.round(val));
  }

  if (typeof val === "object" && val !== null) {
    const values = Object.values(val);
    if (values.length > 0) {
      const firstValue = values[0];
      if (
        typeof firstValue === "string" ||
        typeof firstValue === "number" ||
        typeof firstValue === "boolean" ||
        firstValue === null ||
        firstValue === undefined ||
        React.isValidElement(firstValue)
      ) {
        return firstValue;
      }
      return String(firstValue);
    }
    return "-";
  }

  const processedVal = numberWithSpaces(val);
  if (typeof processedVal === "string" || typeof processedVal === "number") {
    return processedVal;
  }
  return "-";
}

interface FlightTravelPoliciesProps {
  isOpened: boolean;
  onClose: () => void;
  travellers: Traveller[];
  travelPolicies?: any;
}

export function FlightTravelPolicies({
  isOpened,
  onClose,
  travellers,
  travelPolicies,
}: FlightTravelPoliciesProps): ReactElement | null {
  const intl = useIntl();
  const [travelerCode, setTravelerCode] = useState<string | null>(
    travellers && travellers.length > 0
      ? `0-${travellers[0].Email}-${travellers[0].GradeId}`
      : null
  );
  const [expandedRuleIdx, setExpandedRuleIdx] = useState<number | null>(0);
  const isPolicyPassed = policiesPassed(travellers);
  const traveller: Traveller | undefined = travellers.find(
    (t: Traveller, index: number) =>
      `${index}-${t.Email}-${t.GradeId}` === travelerCode
  );
  const users = useSelector((state: AppState) => state.cbt.users);
  const grades = useSelector((state: AppState) => state.cbt.grades);

  useEffect(() => {}, [users]);

  const usersByTravelerId = Object.values(users).reduce((acc, u) => {
    acc[u.Id] = u;
    return acc;
  }, {} as Record<string, any>);

  const getPolicyName = (): string | null => {
    if (!travelPolicies || !traveller?.PolicyId) return null;

    if (Array.isArray(travelPolicies)) {
      const policy = travelPolicies.find(
        (p: any) => p.Id === traveller.PolicyId
      );
      return policy?.Name || null;
    }

    return travelPolicies[traveller.PolicyId]?.Name || null;
  };

  if (!isOpened || !traveller) {
    return null;
  }

  return (
    <Modal
      isActive={isOpened}
      onClose={onClose}
      innerClassName="travel-policies-detail-popup__inner"
      className="travel-policies-detail-popup"
    >
      <div className="travel-policies-detail-popup__content">
        <div className="travel-policies-detail-popup__title">
          <FormattedMessage id="TravelPolicies.travelPoliciesDetail" />
        </div>
        <div
          className={classNames("travel-policies-detail-popup__statusMsg", {
            passed: isPolicyPassed,
            failed: !isPolicyPassed,
          })}
        >
          {isPolicyPassed ? (
            <>
              <PassIcon className="travelers-policies-passed" />
              <FormattedMessage id="TravelPolicies.passedMessage" />
            </>
          ) : (
            <>
              <FailedIcon className="travelers-policies-failed" />
              <FormattedMessage id="TravelPolicies.failedMessage" />
            </>
          )}
        </div>
        <ul className="travel-policies-detail-popup__travelers">
          {travellers.map((currentTraveller: Traveller, index: number) => {
            const {
              Email,
              Result,
              TravellerId,
              Type,
              GradeId,
            } = currentTraveller;
            const userDetail = usersByTravelerId[`${TravellerId}`];
            const FirstName = userDetail?.FirstName;
            const LastName = userDetail?.LastName;
            const name = FirstName && LastName && `${FirstName} ${LastName}`;
            const guestsText = intl.formatMessage({
              id: "General.guestsCountOne",
            });
            const formattedGuestsText =
              guestsText.charAt(0).toUpperCase() + guestsText.slice(1);

            const isGuest = Type === "Guest";
            const selectedTravelerCode = `${index}-${Email}-${GradeId}`;

            return (
              <li key={Email || "Guest"}>
                <div
                  data-email={Email}
                  className={classNames(
                    "travel-policies-detail-popup__traveler",
                    {
                      "travel-policies-detail-popup__traveler--selected":
                        travelerCode === selectedTravelerCode,
                    }
                  )}
                  onClick={() => {
                    setTravelerCode(`${index}-${Email}-${GradeId}`);
                    setExpandedRuleIdx(0);
                  }}
                  role="button"
                >
                  <Tooltip
                    overlay={Email || formattedGuestsText}
                    placement="top"
                  >
                    <div>
                      {isGuest
                        ? grades?.find((grade) => grade.Id === GradeId)?.Name ||
                          formattedGuestsText
                        : name || formattedGuestsText}
                    </div>
                  </Tooltip>
                  {Result ? (
                    <PassIcon className="traveler-policies-passed" />
                  ) : (
                    <FailedIcon className="traveler-policies-failed" />
                  )}
                </div>
              </li>
            );
          })}
        </ul>
        <table className="travel-policies-detail-popup__rules-table">
          {traveller.RulesResults.length === 0 && (
            <div className="travel-policies-detail-popup__rules-table-no-policy">
              <FormattedMessage id="TravelPolicies.noPolicies" />
            </div>
          )}
          {traveller.RulesResults.map((rule: Rule, ruleIdx: number) => {
            const policyName = getPolicyName();
            return (
              <Fragment key={rule.Id}>
                <thead>
                  <tr
                    key={rule.Id}
                    className={classNames(
                      "travel-policies-detail-popup__rules-table__header",
                      { opened: expandedRuleIdx === ruleIdx }
                    )}
                    onClick={() =>
                      setExpandedRuleIdx((idx) =>
                        idx !== ruleIdx ? ruleIdx : null
                      )
                    }
                  >
                    <th className="left">
                      <Img
                        src="/static/images/ico-arrow-down.svg"
                        className={classNames({
                          "opened-rule-arrow": expandedRuleIdx === ruleIdx,
                          "closed-rule-arrow": expandedRuleIdx !== ruleIdx,
                        })}
                      />
                      {policyName || (
                        <>
                          <FormattedMessage id="TravelPolicies.rule" />{" "}
                          {ruleIdx + 1}
                        </>
                      )}
                    </th>
                    <th>
                      <FormattedMessage id="TravelPolicies.policyValue" />
                    </th>
                    <th>
                      <FormattedMessage id="TravelPolicies.bookingValue" />
                    </th>
                    <th>
                      {rule.Result ? (
                        <Img
                          src="/static/images/checkmark-green.svg"
                          className="policy-passed"
                        />
                      ) : (
                        <Img
                          src="/static/images/cross-red.svg"
                          className="travel-policies-detail-popup__failed-state-img policy-failed"
                        />
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(rule.Parameters).map(
                    ([name, param]: [string, RuleParameter]) => {
                      return (
                        <tr
                          key={name}
                          className={classNames(
                            "travel-policies-detail-popup__rules-table__row",
                            { hidden: ruleIdx !== expandedRuleIdx }
                          )}
                        >
                          <td>
                            <span className="travel-policies-detail-popup--first-values">
                              <FormattedMessage
                                id={`TravelPolicies.rule${name}`}
                              />
                            </span>
                          </td>
                          <td className="center">
                            <HumanValue
                              val={param.OriginalValue}
                              reservationVal={param.RequestValue}
                              name={name}
                            />
                          </td>
                          <td className="center">
                            <HumanValue val={param.RequestValue} name={name} />
                          </td>
                          <td>
                            {" "}
                            {param.Result ? (
                              <Img src="/static/images/checkmark-green.svg" />
                            ) : (
                              <Img
                                src="/static/images/cross-red.svg"
                                className="travel-policies-detail-popup__failed-state-img"
                              />
                            )}
                          </td>
                        </tr>
                      );
                    }
                  )}
                </tbody>
              </Fragment>
            );
          })}
        </table>
      </div>
    </Modal>
  );
}

export function keyFromSelectedStream(
  selectedStreamParts: string[],
  pricingDetailKey: string
): string {
  return `${pricingDetailKey}-${selectedStreamParts.slice(1).join("-")}`;
}

interface TravelPolicyStatusProps {
  travelPoliciesResult: TravelPoliciesResultValue;
}

function TravelPolicyStatus({ travelPoliciesResult }: TravelPolicyStatusProps) {
  const intl = useIntl();
  return policiesPassed(travelPoliciesResult.Value || travelPoliciesResult) ? (
    <Img
      src="/static/images/cbt-round-checkmark.svg"
      className="travel-policy-icon travel-policy-passed"
      alt={intl.formatMessage(policiesPassedTitle)}
      title={intl.formatMessage(policiesPassedTitle)}
    />
  ) : (
    <Img
      src="/static/images/cbt-cross.svg"
      className="travel-policy-icon-failed travel-policy-failed"
      alt={intl.formatMessage(policyDenied)}
      title={intl.formatMessage(policyDenied)}
    />
  );
}

export function TravelPoliciesBadges({
  travellers,
  size,
  isLowCost,
  privateFare,
}: {
  travellers: Traveller[] | TravelPoliciesResultValue;
  size?: number;
  isLowCost: boolean;
  privateFare: boolean;
}) {
  const intl = useIntl();
  const sizeClass = size ? `size-18` : "";

  const tooltipStyle = { cursor: "pointer" };
  const tooltipIcon = { marginTop: 1.5, ...tooltipStyle };

  return (
    <div className="flight-prices-policy-badges">
      {travellers && isLowestFare(travellers) && (
        <span style={tooltipIcon}>
          <Tooltip
            placement="bottomLeft"
            overlay={intl.formatMessage(lowestFareTitle)}
            trigger={["hover", "click"]}
            mouseEnterDelay={0.5}
            mouseLeaveDelay={0.2}
          >
            <div>
              <Img
                src="/static/images/cbt-lowest-fare.svg"
                className={`travel-policy-icon lowest-fare ${sizeClass}`}
                alt={intl.formatMessage(lowestFareTitle)}
              />
            </div>
          </Tooltip>
        </span>
      )}
      {travellers && isAcceptableFare(travellers) && (
        <span style={tooltipIcon}>
          <Tooltip
            placement="bottomLeft"
            overlay={intl.formatMessage(acceptableFare)}
            trigger={["hover", "click"]}
            mouseEnterDelay={0.5}
            mouseLeaveDelay={0.2}
          >
            <div>
              <Img
                src="/static/images/cbt-acceptable-fare.svg"
                className={`travel-policy-icon acceptable-fare ${sizeClass}`}
                alt={intl.formatMessage(acceptableFare)}
              />
            </div>
          </Tooltip>
        </span>
      )}
      {privateFare && (
        <span style={tooltipStyle}>
          <PrivateFareIndicator
            sizeClass={sizeClass}
            privateFareMsg={privateFareMsg}
            acceptableFare={acceptableFare}
          />
        </span>
      )}
      {false && (
        <Img
          src="/static/images/cbt-corporate-fare.svg"
          className={`travel-policy-icon corporate-fare ${sizeClass}`}
          alt={intl.formatMessage(corporateFare)}
          title={intl.formatMessage(corporateFare)}
        />
      )}
      {false && (
        <Img
          src="/static/images/cbt-preferred-airline.svg"
          className={`travel-policy-icon preferred-airline ${sizeClass}`}
          alt={intl.formatMessage(preferredAirline)}
          title={intl.formatMessage(preferredAirline)}
        />
      )}
      {false && (
        <Img
          src="/static/images/cbt-government-passenger.svg"
          className={`travel-policy-icon government-passenger ${sizeClass}`}
          alt={intl.formatMessage(governmentPassenger)}
          title={intl.formatMessage(governmentPassenger)}
        />
      )}
      {false && (
        <Img
          src="/static/images/cbt-seaman-fare.svg"
          className={`travel-policy-icon seaman-fare no-margin ${sizeClass}`}
          alt={intl.formatMessage(seamanFare)}
          title={intl.formatMessage(seamanFare)}
        />
      )}
      {isLowCost && (
        <span style={tooltipStyle}>
          <LowCostIndicator />
        </span>
      )}
    </div>
  );
}

export default function TravelPolicies({
  travelPolicies,
  travelPoliciesResult,
  showInline,
  isHotels = false,
  isLowCostFlag,
  isPrivateFareFlag,
  isTwoOneWay,
}: {
  travelPoliciesResult: TravelPoliciesResultValue | Record<string, never>;
  travelPolicies: any;
  showInline?: boolean;
  isLowCostFlag: boolean;
  isHotels?: boolean;
  isPrivateFareFlag: boolean;
  isTwoOneWay: boolean;
}) {
  const [popupOpened, setPopupOpened] = useState(false);
  const [detail, setDetail] = useState<TravelPoliciesResultValue | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(false);
  const customerUsername = useSelector((state: AppState) => state.user.email);
  const cbtApiUrl = useSelector(
    (state: AppState) =>
      state.storage.frontendSettings?.dealerCorporateSettings?.cbtApiUrl
  );

  useEffect(() => {
    if (
      !popupOpened ||
      !travelPoliciesResult?.Id ||
      travelPoliciesResult.Value
    ) {
      return;
    }

    let active = true;
    setDetail(null);
    setDetailError(false);
    setDetailLoading(true);
    (async () => {
      const cbtToken = await getCbtToken();
      const response =
        cbtToken && customerUsername
          ? await getPolicyResultGOL({
              policyResolvedId: travelPoliciesResult.Id,
              cbtToken,
              customerUsername,
              cbtApiUrl,
            })
          : null;
      if (active) {
        setDetail(response?.success ? response.data : null);
        setDetailError(!response?.success);
        setDetailLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [
    popupOpened,
    travelPoliciesResult?.Id,
    travelPoliciesResult?.Value,
    customerUsername,
    cbtApiUrl,
  ]);

  const { oneWayFare } = defineMessages({
    oneWayFare: formattedMessageParams("FlightOffer.OneWayIndicator.tooltip"),
  });
  const policyResult = travelPoliciesResult as TravelPoliciesResultValue;
  const fullResult = policyResult?.Value
    ? policyResult
    : detail?.Id === policyResult?.Id
    ? detail
    : null;
  const openDetail = () => {
    setDetailLoading(Boolean(policyResult?.Id && !policyResult?.Value));
    setPopupOpened(true);
  };
  return (
    <>
      {fullResult?.Value && (
        <FlightTravelPolicies
          isOpened={popupOpened}
          onClose={() => setPopupOpened(false)}
          travellers={fullResult.Value}
          travelPolicies={fullResult.TravelPolicies || travelPolicies}
        />
      )}
      {popupOpened && !fullResult?.Value?.length && (
        <Modal isActive={popupOpened} onClose={() => setPopupOpened(false)}>
          {detailLoading ? (
            <FormattedMessage id="Loader.wait" />
          ) : detailError ? (
            <div>
              <p>
                <FormattedMessage id="Search.unavailable" />
              </p>
              <a href="/">
                <FormattedMessage id="BookingPriceConfirmation.newSearch" />
              </a>
            </div>
          ) : (
            <FormattedMessage id="TravelPolicies.noPolicies" />
          )}
        </Modal>
      )}
      {true && (
        <div
          style={{
            margin: !showInline ? "20px 0" : "5px 0",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <TravelPoliciesBadges
            travellers={policyResult?.Value || policyResult}
            isLowCost={isLowCostFlag}
            privateFare={isPrivateFareFlag}
          />
          {isTwoOneWay && <OneWayIndicator {...oneWayFare} />}
        </div>
      )}
      {travelPoliciesResult && (
        <div
          role="button"
          className={classNames("flight-prices-links-travel-policy", {
            "flight-prices-links-travel-policy-inline": showInline && !isHotels,
          })}
          tabIndex={0}
          onClick={openDetail}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              openDetail();
            }
          }}
        >
          <TravelPolicyStatus travelPoliciesResult={travelPoliciesResult} />
          <FormattedMessage id="FlightOffer.travelPolicy" />
        </div>
      )}
    </>
  );
}
